import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAcademySlug } from '../../../hooks/useAcademySlug';
import { Calendar, Clock, Users, DollarSign, User } from 'lucide-react';
import type { Academy, Lesson, Term, Coach, ProRataPreviewResponse } from '@court-sync/shared';
import { isCampFullPackageFromDaySelection } from '@court-sync/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Alert, AlertDescription } from '../../../components/ui/Alert';
import { formatTimeHHMM } from '../../../utils/timeFormat';
import { getPublicLessonEnrollmentLabel } from '../../../utils/publicLessonEnrollmentLabel';
import { DAY_NAMES, shortDateOptions } from './constants';
import { cn } from '@/lib/utils';
import { lessonDescriptionTextClassName } from '../../../utils/lessonDisplay';
import { PublicLessonCardImage } from '../../../components/public/PublicLessonCardImage';
import { usePublicBookingStore } from '../../../store/publicBooking';
import type { CampBookingMode, GroupBookingMode } from '../../../store/publicBooking';
import { supportsGroupSessionBooking, isGroupDropInPublicBooking } from './publicBookingSessionMode';
import {
  appendPublicBookingWizardContextQueries,
  publicBookingStepPath,
} from './bookingPaths';

function parseYmdToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return new Date(ymd);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function formatCoachName(coach: Coach): string {
  return `${coach.firstName} ${coach.lastName}`.trim();
}

export interface PublicBookingLessonCardProps {
  lesson: Lesson;
  academy: Academy | null;
  term: Term | null;
  lessonCoaches: Coach[];
  formatDate: (
    date: Date,
    region?: string | null,
    options?: Intl.DateTimeFormatOptions
  ) => string;
  /** YYYY-MM-DD dates chosen for per-day camp booking; drives sidebar total. */
  selectedSessionDates?: string[];
  /** Server preview for group term pro-rata pricing. */
  proRataPreview?: ProRataPreviewResponse | null;
}

export function PublicBookingLessonCard({
  lesson,
  academy,
  term,
  lessonCoaches,
  formatDate,
  selectedSessionDates = [],
  proRataPreview = null,
}: PublicBookingLessonCardProps) {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const userType = usePublicBookingStore((s) => s.userType);
  const invitationToken = usePublicBookingStore((s) => s.invitationToken);
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const campBookingMode = usePublicBookingStore((s) => s.campBookingMode);
  const setCampBookingMode = usePublicBookingStore((s) => s.setCampBookingMode);
  const groupBookingMode = usePublicBookingStore((s) => s.groupBookingMode);
  const setGroupBookingMode = usePublicBookingStore((s) => s.setGroupBookingMode);
  const effectiveCampMode: CampBookingMode = campBookingMode ?? 'daily';
  const effectiveGroupMode: GroupBookingMode = groupBookingMode ?? 'full';

  const showGroupSessionOption = supportsGroupSessionBooking(lesson, term?.isActive);

  const daysLabel =
    lesson.daysOfWeek && lesson.daysOfWeek.length > 0
      ? lesson.daysOfWeek
          .map((d) => (d >= 0 && d <= 6 ? DAY_NAMES[d] : ''))
          .filter(Boolean)
          .join(', ')
      : '';

  const isCamp = lesson.lessonType === 'camp';
  const isPrivate = lesson.lessonType === 'private';
  const isPerDayCamp = isCamp && effectiveCampMode === 'daily';
  const isGroupSessionMode =
    showGroupSessionOption && effectiveGroupMode === 'session';
  const isGroupDropIn = isGroupDropInPublicBooking(lesson, groupBookingMode);
  const unitDayPrice = isPerDayCamp
    ? lesson.campDayPrice
    : isGroupSessionMode
      ? lesson.sessionPrice
      : lesson.pricePerSession;
  const fullCampPrice = isCamp ? lesson.campFullPrice ?? lesson.price : lesson.price;
  const sortedSessionDates = [...selectedSessionDates].sort();
  const selectedSessionCount = sortedSessionDates.length;
  const usesFullCampPackage =
    isPerDayCamp && isCampFullPackageFromDaySelection(lesson, sortedSessionDates);
  const totalForSelection =
    usesFullCampPackage && fullCampPrice != null
      ? fullCampPrice
      : (isPerDayCamp || isGroupSessionMode) && unitDayPrice != null && selectedSessionCount > 0
        ? unitDayPrice * selectedSessionCount
        : null;

  const isGroupProRata =
    !isCamp &&
    !isGroupSessionMode &&
    lesson.lessonType === 'group' &&
    lesson.pricingType !== 'per_session' &&
    proRataPreview?.academyProRataEnabled &&
    proRataPreview.isProRated;

  const displayPrice =
    isGroupProRata
      ? proRataPreview.proRataPrice
      : effectiveCampMode === 'full' && isCamp
        ? fullCampPrice ?? 0
        : usesFullCampPackage && fullCampPrice != null
          ? fullCampPrice
          : totalForSelection != null
            ? totalForSelection
            : isPerDayCamp && unitDayPrice != null
              ? unitDayPrice
              : isGroupSessionMode && unitDayPrice != null
                ? unitDayPrice
                : lesson.price;
  const priceLabel =
    isGroupProRata
      ? `${proRataPreview.remainingSessions} of ${proRataPreview.totalSessions} sessions`
      : effectiveCampMode === 'full' && isCamp
        ? 'Full camp package'
        : usesFullCampPackage
          ? 'Full camp package'
          : totalForSelection != null
            ? isGroupDropIn
              ? 'Drop-in session'
              : `${selectedSessionCount} session${selectedSessionCount === 1 ? '' : 's'}`
            : isPerDayCamp
              ? 'Per day'
              : isGroupSessionMode
                ? 'Per session'
                : 'Full term';
  const priceSubLabel =
    isGroupProRata
      ? `Full term $${proRataPreview.fullPrice}`
      : usesFullCampPackage && lesson.campDayPrice != null
        ? `${lesson.campDayPrice} per day if booking days separately`
        : totalForSelection != null && unitDayPrice != null && !isGroupDropIn
          ? `$${unitDayPrice} × ${selectedSessionCount} session${selectedSessionCount === 1 ? '' : 's'}`
          : effectiveCampMode === 'full' && isCamp && lesson.campDayPrice != null
            ? `${lesson.campDayPrice} per day if booking days separately`
            : effectiveGroupMode === 'full' &&
                showGroupSessionOption &&
                lesson.sessionPrice != null
              ? `${lesson.sessionPrice} per session if booking sessions separately`
              : undefined;

  const campDateRangeLabel =
    isCamp && lesson.startDate && lesson.endDate
      ? `${formatDate(lesson.startDate, academy?.region, shortDateOptions)} – ${formatDate(
          lesson.endDate,
          academy?.region,
          shortDateOptions
        )}`
      : null;

  return (
    <Card className="h-fit sticky top-8 shadow-sm overflow-hidden">
      <PublicLessonCardImage lesson={lesson} />
      <CardHeader className="gap-3 border-b border-border bg-muted/30 p-6 sm:p-7">
        <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
          <CardTitle className="text-lg sm:text-xl font-semibold text-foreground leading-snug break-words pr-1">
            {lesson.name}
          </CardTitle>
          {isPrivate && (
            <Badge
              variant="secondary"
              className="shrink-0 whitespace-nowrap mt-0.5 bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-100"
            >
              Private lesson
            </Badge>
          )}
          {lesson.isInvitationOnly && (
            <Badge variant="secondary" className="shrink-0 whitespace-nowrap mt-0.5">
              Invitation only
            </Badge>
          )}
        </div>
        {campDateRangeLabel && (
          <p className="text-sm font-medium text-foreground/90">{campDateRangeLabel}</p>
        )}
        {lesson.description && (
          <CardDescription
            className={cn(
              'text-sm leading-relaxed text-muted-foreground pt-1',
              lessonDescriptionTextClassName
            )}
          >
            {lesson.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-5 sm:space-y-6 p-6 sm:p-7 pt-6">
        {term && (
          <div className="flex gap-3 text-sm text-muted-foreground rounded-lg border border-border/80 bg-card px-3 py-3 sm:px-4">
            <Calendar className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium text-foreground">{term.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatDate(term.startDate, academy?.region, shortDateOptions)} -{' '}
                {formatDate(term.endDate, academy?.region, shortDateOptions)}
              </p>
            </div>
          </div>
        )}
        {lessonCoaches.length > 0 && (
          <div className="flex gap-3 text-sm text-muted-foreground rounded-lg border border-border/80 bg-card px-3 py-3 sm:px-4">
            {lessonCoaches.length === 1 && lessonCoaches[0]?.avatarUrl ? (
              <img
                src={lessonCoaches[0].avatarUrl}
                alt=""
                width={40}
                height={40}
                className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-border"
                loading="lazy"
              />
            ) : (
              <User className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
            )}
            <div className="min-w-0">
              <p className="font-medium text-foreground">
                {lessonCoaches.map(formatCoachName).join(', ')}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {lessonCoaches.length === 1 ? 'Coach' : 'Coaches'}
              </p>
            </div>
          </div>
        )}
        {daysLabel && (
          <div className="flex gap-3 text-sm text-muted-foreground rounded-lg border border-border/80 bg-card px-3 py-3 sm:px-4">
            <Calendar className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium text-foreground">{daysLabel}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {lesson.daysOfWeek && lesson.daysOfWeek.length > 1 ? 'Days of week' : 'Day of week'}
              </p>
            </div>
          </div>
        )}
        <div className="flex gap-3 text-sm text-muted-foreground rounded-lg border border-border/80 bg-card px-3 py-3 sm:px-4">
          <Clock className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="font-medium text-foreground">
              {formatTimeHHMM(lesson.startTime)} - {formatTimeHHMM(lesson.endTime)}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Lesson time</p>
          </div>
        </div>
        {lesson.maxCapacity !== -1 && (
          <div className="flex gap-3 text-sm text-muted-foreground rounded-lg border border-border/80 bg-card px-3 py-3 sm:px-4">
            <Users className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium text-foreground">
                {getPublicLessonEnrollmentLabel(lesson)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Enrollment</p>
            </div>
          </div>
        )}
        {isCamp && (
          <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/20">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Camp booking</p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="camp-booking-mode"
                  checked={effectiveCampMode === 'daily'}
                  onChange={() => setCampBookingMode('daily')}
                  className="text-primary"
                />
                <span>Book by day — choose dates in the next step</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="camp-booking-mode"
                  checked={effectiveCampMode === 'full'}
                  onChange={() => setCampBookingMode('full')}
                  className="text-primary"
                />
                <span>Book full camp package</span>
              </label>
            </div>
          </div>
        )}
        {showGroupSessionOption && (
          <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/20">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Term booking</p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="group-booking-mode"
                  checked={effectiveGroupMode === 'full'}
                  onChange={() => setGroupBookingMode('full')}
                  className="text-primary"
                />
                <span>Book full term{isGroupProRata ? ' (pro-rata)' : ''}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="group-booking-mode"
                  checked={effectiveGroupMode === 'session'}
                  onChange={() => setGroupBookingMode('session')}
                  className="text-primary"
                />
                <span>Book next available session (drop-in)</span>
              </label>
            </div>
            {effectiveGroupMode === 'session' && (
              <p className="text-xs text-muted-foreground">
                The next upcoming session is selected automatically — no date picker.
              </p>
            )}
          </div>
        )}
        {isGroupDropIn && (
          <div className="flex gap-3 items-start text-sm text-muted-foreground border-t border-border pt-5">
            <Calendar className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">Next session</p>
              {selectedSessionCount > 0 ? (
                <p className="text-sm text-foreground mt-1">
                  {formatDate(parseYmdToLocalDate(sortedSessionDates[0]!), academy?.region, shortDateOptions)}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">No upcoming sessions available.</p>
              )}
            </div>
          </div>
        )}
        {isPerDayCamp && selectedSessionCount > 0 && (
          <div className="flex gap-3 items-start text-sm text-muted-foreground border-t border-border pt-5">
            <Calendar className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-foreground">Selected camp days</p>
                {userType && academySlug && lessonId && (
                  <Link
                    to={appendPublicBookingWizardContextQueries(
                      publicBookingStepPath(academySlug, lessonId, 'sessions'),
                      { invitationToken, manualInviteToken }
                    )}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Change dates
                  </Link>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-2 mt-0.5">
                {selectedSessionCount} date{selectedSessionCount === 1 ? '' : 's'}
              </p>
              <ul className="max-h-40 overflow-y-auto space-y-1.5 text-sm text-foreground pr-1">
                {sortedSessionDates.map((ymd) => (
                  <li key={ymd}>
                    {formatDate(parseYmdToLocalDate(ymd), academy?.region, shortDateOptions)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        <div className="flex gap-3 items-start border-t border-border pt-5 text-lg font-semibold text-green-600 dark:text-green-500">
          <DollarSign className="h-7 w-7 shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-2xl font-bold tracking-tight">${displayPrice}</p>
            <p className="text-xs font-normal text-muted-foreground mt-1">{priceLabel}</p>
            {priceSubLabel && (
              <p className="text-xs font-normal text-muted-foreground mt-1.5">{priceSubLabel}</p>
            )}
          </div>
        </div>
        {lesson.maxCapacity !== -1 &&
          !academy?.allowEnrolmentWhenFull &&
          (lesson.currentEnrollment || 0) >= lesson.maxCapacity && (
            <Alert variant="destructive">
              <AlertDescription>
                {academy?.waitlistEnabled
                  ? 'This lesson is fully booked. You can join the waitlist below.'
                  : 'This lesson is fully booked. Please choose another lesson.'}
              </AlertDescription>
            </Alert>
          )}
      </CardContent>
    </Card>
  );
}
